import { z } from 'zod'
import { http } from '@/shared/api/axios'
import {
  IncidentTaxonomyResponseSchema, type IncidentTaxonomy,
  IncidentDetailResponseSchema, type IncidentDetail,
  PaginatedIncidentsResponseSchema, type PaginatedIncidentsResponse,
  IncidentLocationsResponseSchema, type IncidentLocationsResponse,
  UpdateIncidentStatusResponseSchema, type UpdateIncidentStatusResponse,
  AddFollowUpResponseSchema, type AddFollowUpResponse,
  IncidentReportHistorySchema, type IncidentReportHistory,
  IncidentReportRevisionTextSchema, type IncidentReportRevisionText,
  IncidentReportRecipientSchema, type IncidentReportRecipient,
  type EditIncidentReportBody,
} from './types'

export interface IncidentListParams {
  pageNumber?: number
  pageSize?: number
  statusFilter?: string
  severityFilter?: string
  fromDate?: string
  toDate?: string
  shiftId?: number
  guardUserId?: number
  clientId?: number
  siteId?: number
  categoryId?: number
  subtypeId?: number
  search?: string
  statusChangedFrom?: string
  sort?: 'lastActivity' | 'occurred'
}

export interface IncidentClientVisibilityRow {
  subtypeId: number
  categoryName: string
  subtypeName: string
  defaultSeverity: string
  isClientVisible: boolean
}

export const incidentApi = {
  getTaxonomy: async (): Promise<IncidentTaxonomy> => {
    const { data } = await http.get('/v1/incidents/taxonomy')
    return IncidentTaxonomyResponseSchema.parse(data)
  },

  getClientVisibility: async (): Promise<IncidentClientVisibilityRow[]> => {
    const { data } = await http.get('/v1/incidents/taxonomy/client-visibility')
    return data.rows
  },

  setClientVisibility: async (subtypeId: number, isClientVisible: boolean): Promise<void> => {
    await http.patch(`/v1/incidents/taxonomy/subtypes/${subtypeId}/client-visibility`, {
      isClientVisible,
    })
  },

  listMine: async (params: { pageNumber?: number; pageSize?: number; statusFilter?: string }): Promise<PaginatedIncidentsResponse> => {
    const { data } = await http.get('/v1/incidents/me', { params })
    return PaginatedIncidentsResponseSchema.parse(data)
  },

  list: async (params: IncidentListParams, options?: { skipErrorToast?: boolean }): Promise<PaginatedIncidentsResponse> => {
    const { data } = await http.get('/v1/incidents', {
      params,
      ...(options?.skipErrorToast ? { _skipErrorToast: true } : {}),
    } as never)
    return PaginatedIncidentsResponseSchema.parse(data)
  },

  detail: async (id: number): Promise<IncidentDetail> => {
    const { data } = await http.get(`/v1/incidents/${id}`, { _skipErrorToast: true } as never)
    return IncidentDetailResponseSchema.parse(data)
  },

  references: async (ids: number[]): Promise<{ id: number; reference: string }[]> => {
    const { data } = await http.get('/v1/incidents/references', {
      params: { ids },
      paramsSerializer: { indexes: null },
      _skipErrorToast: true,
    } as never)
    return z.array(z.object({ id: z.number(), reference: z.string() })).parse(data)
  },

  locations: async (params: { statusFilter?: string; fromDate?: string; toDate?: string }): Promise<IncidentLocationsResponse> => {
    const { data } = await http.get('/v1/incidents/locations', { params })
    return IncidentLocationsResponseSchema.parse(data)
  },

  updateStatus: async (id: number, body: { newStatus: string; note?: string }): Promise<UpdateIncidentStatusResponse> => {
    const { data } = await http.patch(`/v1/incidents/${id}/status`, body)
    return UpdateIncidentStatusResponseSchema.parse(data)
  },

  addFollowUp: async (id: number, body: { note: string; attachmentIds: number[] }): Promise<AddFollowUpResponse> => {
    const { data } = await http.post(`/v1/incidents/${id}/follow-ups`, body)
    return AddFollowUpResponseSchema.parse(data)
  },

  editReport: async (id: number, body: EditIncidentReportBody): Promise<{ id: number; reportRevision: number }> => {
    const { data } = await http.put(`/v1/incidents/${id}/report`, body, { _skipErrorToast: true } as never)
    return data
  },

  reportHistory: async (id: number): Promise<IncidentReportHistory> => {
    const { data } = await http.get(`/v1/incidents/${id}/report/history`, { _skipErrorToast: true } as never)
    return IncidentReportHistorySchema.parse(data)
  },

  reportRevision: async (id: number, revisionNo: number): Promise<IncidentReportRevisionText> => {
    const { data } = await http.get(`/v1/incidents/${id}/report/revisions/${revisionNo}`, { _skipErrorToast: true } as never)
    return IncidentReportRevisionTextSchema.parse(data)
  },

  reportRecipients: async (id: number): Promise<IncidentReportRecipient[]> => {
    const { data } = await http.get(`/v1/incidents/${id}/report/recipients`, { _skipErrorToast: true } as never)
    return z.array(IncidentReportRecipientSchema).parse(data)
  },

  sendReport: async (id: number, body: { recipients: string[]; message?: string }): Promise<{ sendId: number; recipientCount: number }> => {
    const { data } = await http.post(`/v1/incidents/${id}/report/send`, body, { _skipErrorToast: true } as never)
    return data
  },

  reportPdfUrl: (id: number) => `/v1/incidents/${id}/report/pdf`,

  sentReportPdfUrl: (id: number, sendId: number) => `/v1/incidents/${id}/report/sends/${sendId}/pdf`,

  uploadAttachment: async (file: File, type: string): Promise<{ attachmentId: number; fileStorageId: string }> => {
    const form = new FormData()
    form.append('file', file)
    form.append('type', type)
    const { data } = await http.post('/v1/incidents/attachments', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data as { attachmentId: number; fileStorageId: string }
  },
}
